import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import { TokenResponse } from '@openid/appauth';
import { parseJwt } from '@utils/common';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import type React from 'react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CONFIG } from '../config';
import type { Organization } from '../interfaces/organization';
import { Box } from '../ovComponents/1-primative/box/box';
import { Typography } from '../ovComponents/1-primative/typography/typography';
import { Button } from '../ovComponents/2-component/button/button';
import { CircularProgress } from '../ovComponents/2-component/circularProgress/circularProgress';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../ovComponents/2-component/dialog/dialog';
import { PublicOrganizationSelectDialog } from '../ovComponents/3-pattern/publicOrganizationSelectDialog/publicOrganizationSelectDialog';
import {
  modifyFavIcon,
  modifyTabTitle,
  setOrgPublicSettings,
} from '../utils/browserTabUpdates';
import { clientHostedLogout, useAuth } from './AuthProvider';
import auth0, {
  auth0RefreshToken,
  getOrganizationKeyFromURL,
  JWT_CLAIMS_NAMESPACE,
} from './auth0Client';
import { LAST_ACTIVITY_TIME_STAMP } from './inActivityTimeoutProvider';
import { useAuthContext } from './ovApolloProvider';

const ONE_MINUTE_IN_MS = 60_000;

type DecodedToken = {
  exp: number;
  [key: string]: string | number | [];
};

const isTokenExpired = (token: string, checkIfExpiring = false) => {
  try {
    const decodedToken: DecodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    const expirationBuffer = checkIfExpiring ? 60 : 0;

    const externalToken = decodedToken[
      'https://onevest.com/externalClientToken'
    ] as string;
    if (
      externalToken &&
      typeof externalToken === 'string' &&
      externalToken.length > 10
    ) {
      try {
        const externalDecodedToken: DecodedToken = jwtDecode(externalToken);
        const exp = externalDecodedToken?.exp;

        if (typeof exp === 'number' && exp < currentTime + expirationBuffer) {
          return true;
        }
      } catch {
        return true;
      }
    }

    // Fallback to main token if no external token or external token is not expired
    const exp = decodedToken?.exp;
    return typeof exp === 'number'
      ? exp < currentTime + expirationBuffer
      : true;
  } catch {
    console.error('Client Hosted Login Expired');
    return true;
  }
};

export const BASIC_ME = gql`
  query basicMe {
    me {
      id
      firstName
      lastName
      email
      permissions
    }
  }
`;

export const FETCH_ORGANIZATION_PUBLIC_SETTINGS = gql`
  query fetchOrganizationPublicSettings(
    $input: FetchOrganizationPublicSettingsInput!
  ) {
    fetchOrganizationPublicSettings(input: $input) {
      subdomain
      faviconLink
      browserTabTitle
      clientHostedLoginClientId
      clientHostedLoginURL
      clientHostedLoginScope
      authenticationType
      enterpriseConnectionName
      useHostedLogin
      logoutUrl
    }
  }
`;

export const EXCHANGE_CLIENT_TOKEN = gql`
  mutation exchangeClientToken($input: ExchangeClientTokenInput!) {
    exchangeClientToken(input: $input) {
      accessToken
    }
  }
`;

const Auth0Provider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { loginPing, refreshToken, handleCallback, loginWithSSOSession } =
    useAuth();
  const { t } = useTranslation('authentication');
  const {
    setIsEmbedded,
    setPermissions,
    appLogout,
    setSettings,
    settings,
    getTokenSilentlyRef,
  } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [openOrganizationSelectDialog, setOpenOrganizationSelectDialog] =
    useState(false);
  const [accessibleOrgs, setAccessibleOrgs] = useState<string[]>();
  const [openSignUpNotAllowedDialog, setSignUpNotAllowedDialog] =
    useState(false);
  const [openLinkExpiredDialog, setOpenLinkExpiredDialog] = useState(false);
  const [loginParams, setLoginParams] = useState({});

  const localStorageFavIcon = window.localStorage.getItem('faviconLink') || '';
  const localStorageBrowserTitle =
    window.localStorage.getItem('browserTabTitle') || '';
  const [faviconLink, setFaviconLink] = useState(localStorageFavIcon);
  const [browserTabTitle, setBrowserTabTitle] = useState(
    localStorageBrowserTitle,
  );

  const [basicMe] = useLazyQuery(BASIC_ME);
  const [fetchOrganizationPublicSettings] = useLazyQuery(
    FETCH_ORGANIZATION_PUBLIC_SETTINGS,
  );
  const [exchangeClientTokenMutation] = useMutation(EXCHANGE_CLIENT_TOKEN);

  const refreshTokenPromiseRef = useRef<Promise<string> | null>(null);
  const oneVestExchangedClientTokenRef = useRef<string | undefined>(undefined);
  const externalTokenResponseRef = useRef<TokenResponse | undefined>(undefined);

  const clientHostedLoginToken =
    window.localStorage.getItem('clientHostedLoginToken') || undefined;
  const multipleOrgChosen =
    window.localStorage.getItem('multipleOrgChosen') === 'true';
  const isOneWealthDomain =
    window.location.host.includes('onevest.') ||
    window.location.host.includes('onewealth.');

  const queryParams = new URLSearchParams(window.location.search);
  const invitiation = queryParams.get('invitation');
  const organization = queryParams.get('organization');
  const organizationName = queryParams.get('organization_name') || undefined;
  const state = queryParams.get('state');
  const connection = queryParams.get('connection');
  const allowPopup = queryParams.get('allow_popup');
  const externalAccessTokenParams = queryParams.get('accessToken');
  const authCode = queryParams.get('authCode');

  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  const linkExpiredError = hashParams.get('error');
  const linkExpiredErrorDescription = hashParams.get('error_description');

  const getOrgKeyFromPath = () => {
    const pathSegments = window.location.pathname.split('/');
    if (pathSegments[1] === 'org' && pathSegments[2]) {
      sessionStorage.setItem('localDevOrgKey', pathSegments[2]);
      return pathSegments[2];
    }

    // If we're on localhost and not on /org path, check session storage
    // This caters to /clients/437402342703742 path once the user refreshes
    // in logged-in state
    if (window.location.hostname === 'localhost') {
      return sessionStorage.getItem('localDevOrgKey');
    }
  };

  const isUsingLocalENV = () => {
    const result: {
      isLocalENV: boolean;
      orgKey: string | undefined;
      localAuth0Redirect: string | undefined;
      organizationCustomUrl: string | undefined;
    } = {
      isLocalENV: false,
      orgKey: undefined,
      localAuth0Redirect: undefined,
      organizationCustomUrl: undefined,
    };

    const isLocal = window.location.hostname === 'localhost';
    const orgKeyFromPath = getOrgKeyFromPath();

    if (
      isLocal &&
      typeof orgKeyFromPath === 'string' &&
      orgKeyFromPath.length > 0
    ) {
      result.isLocalENV = true;
      result.orgKey = orgKeyFromPath;
      result.organizationCustomUrl = `${orgKeyFromPath}.staging.onevest.com`;
      result.localAuth0Redirect = `http://localhost:3000/org/${orgKeyFromPath}`;

      console.log('RUNNING LOCAL ENV');
    }

    return result;
  };

  /**
   * If it's a isOneWealthDomain, we can safely assume that the organizationKey (aka subdomain) will be present in the first part of the host
   * e.g.: admin.staging.onevest.com, where `admin` would be the `organizationKey`
   *
   * In cases where it's not a isOneWealthDomain, meaning it's a custom domain/url, we need to fetch the organizationKey asynchronously
   * e.g.: wealth.partner.com
   */
  const localDevEnv = isUsingLocalENV();
  const { isLocalENV, orgKey, localAuth0Redirect } = localDevEnv;

  const { auth0RedirectUri, organizationKey } = isOneWealthDomain
    ? getOrganizationKeyFromURL(organizationName)
    : isLocalENV === true && orgKey && localAuth0Redirect
      ? { auth0RedirectUri: localAuth0Redirect, organizationKey: orgKey }
      : { auth0RedirectUri: window.location.origin, organizationKey: null };

  const { organizationCustomUrl = window.location.host } = localDevEnv;

  const publicSettingsInput = {
    ...(organizationKey ? { subdomain: organizationKey } : {}),
    customUrl: organizationCustomUrl,
  };

  if (invitiation && organization) {
    window.location.href = `https://${CONFIG.auth.domain}/authorize?invitation=${invitiation}&organization=${organization}&response_type=token&client_id=${CONFIG.auth.clientId}&redirect_uri=${auth0RedirectUri}`;
  }

  const fetchPermissions = async (token: string) => {
    const basicMeResp = await basicMe({
      context: { headers: { authorization: `Bearer ${token}` } },
      variables: { skipErrorHandler: true },
      onError: () => {
        setLoading(false);
        setSignUpNotAllowedDialog(true);
      },
    });

    const userPermissions = basicMeResp?.data?.me?.permissions;
    if (userPermissions) setPermissions(userPermissions);
    return !!userPermissions;
  };

  const onOrgSelect = (org: Organization) => {
    auth0
      .loginWithRedirect({
        authorizationParams: {
          redirect_uri: auth0RedirectUri,
          organization: org.subdomain,
        },
      })
      .then(() => {
        window.localStorage.setItem('multipleOrgChosen', 'true');
      });
  };

  const exchangeClientToken = async () => {
    const resp = await fetchOrganizationPublicSettings({
      variables: { input: publicSettingsInput },
    });
    const orgSettings = resp?.data?.fetchOrganizationPublicSettings;

    console.log({
      event: 'EXCHANGE_CLIENT_TOKEN',
      organizationKey,
      orgSubdomain: orgSettings?.subdomain,
    });
    const sessionId = crypto.randomUUID().replace(/-/g, '');

    const exchangeClientTokenResponse = await exchangeClientTokenMutation({
      variables: {
        input: {
          organizationId: orgSettings?.subdomain ?? organizationKey,
          externalClientToken: externalTokenResponseRef.current?.accessToken,
          externalClientRefreshToken:
            externalTokenResponseRef.current?.refreshToken,
          sessionId,
          forceCacheUpdate: orgSettings?.useHostedLogin,
        },
      },
    });

    const token: string =
      exchangeClientTokenResponse?.data?.exchangeClientToken?.accessToken;

    if (!token) {
      refreshTokenPromiseRef.current = null;
      throw new Error('No access token received from token exchange');
    }

    oneVestExchangedClientTokenRef.current = token;
    refreshTokenPromiseRef.current = null;
    if (orgSettings?.useHostedLogin) {
      window.localStorage.setItem('clientHostedLoginToken', token);
    }
    await fetchPermissions(token);
    setLoading(false);
    return token;
  };

  const refreshExternalAccessToken = async () => {
    const externalAccessToken = externalTokenResponseRef.current?.accessToken;
    if (
      externalAccessToken &&
      isTokenExpired(externalAccessToken, true) &&
      externalTokenResponseRef.current?.refreshToken
    ) {
      const resp = await fetchOrganizationPublicSettings({
        variables: { input: publicSettingsInput },
      });
      const orgSettings = resp?.data?.fetchOrganizationPublicSettings;

      externalTokenResponseRef.current = await refreshToken({
        issuerUrl: orgSettings?.clientHostedLoginURL ?? '',
        redirectUrl: auth0RedirectUri,
        clientId: orgSettings?.clientHostedLoginClientId ?? '',
        refreshTokenStr: externalTokenResponseRef.current?.refreshToken ?? '',
      });
    }
    const token = await exchangeClientToken();
    return token;
  };

  const getTokenSilently = async () => {
    const isAuth0Authenticated = await auth0.isAuthenticated();
    if (isAuth0Authenticated) return auth0.getTokenSilently();

    if (
      oneVestExchangedClientTokenRef.current &&
      !isTokenExpired(oneVestExchangedClientTokenRef.current, true)
    ) {
      return oneVestExchangedClientTokenRef.current;
    }

    if (externalTokenResponseRef.current?.accessToken) {
      if (refreshTokenPromiseRef.current) {
        return refreshTokenPromiseRef.current;
      }

      const tokenPromise = Promise.race([
        refreshExternalAccessToken(),
        new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error('Token exchange timeout')),
            ONE_MINUTE_IN_MS,
          );
        }),
      ]) as Promise<string>;

      refreshTokenPromiseRef.current = tokenPromise;

      // Return promise that ensures cleanup in all cases
      return tokenPromise.catch((error) => {
        datadogLogs.logger.error('Token exchange failed or timed out:', {
          error,
        });
        if (refreshTokenPromiseRef.current === tokenPromise) {
          refreshTokenPromiseRef.current = null;
        }
        throw error;
      });
    }

    return '';
  };

  const handleHostedLogin = async () => {
    console.log('Initiating Hosted Login process to get Auth code...');

    const resp = await fetchOrganizationPublicSettings({
      variables: { input: publicSettingsInput },
    });

    const orgSettings = resp?.data?.fetchOrganizationPublicSettings;

    const clientHostedLoginURL = orgSettings?.clientHostedLoginURL;
    const clientHostedLoginClientId = orgSettings?.clientHostedLoginClientId;
    const clientHostedLoginScope = orgSettings?.clientHostedLoginScope;

    if (
      !clientHostedLoginURL ||
      !clientHostedLoginClientId ||
      !clientHostedLoginScope
    ) {
      datadogLogs.logger.error('Hosted Login error', {
        loginError:
          'Client Id and hostedLoginURL are required to initiate Hosted Login',
        organization: organizationKey,
      });
      throw new Error(
        'Client Id and hostedLoginURL are required to initiate Hosted Login',
      );
    }

    setSettings({
      ...settings,
      clientHostedLoginClientId,
      clientHostedLoginURL,
      logoutUrl: orgSettings?.logoutUrl,
      subdomain: orgSettings?.subdomain ?? organizationKey,
      useHostedLogin: orgSettings?.useHostedLogin,
      clientHostedLoginRedirectUri:
        orgSettings?.clientHostedLoginRedirectUri || window.location.origin,
    });

    await loginPing({
      issuerUrl: clientHostedLoginURL,
      redirectUrl: auth0RedirectUri,
      clientId: clientHostedLoginClientId,
      loginScope: clientHostedLoginScope,
    });
  };

  const handleExchangeClientCode = async () => {
    const resp = await fetchOrganizationPublicSettings({
      variables: { input: publicSettingsInput },
    });

    const orgSettings = resp?.data?.fetchOrganizationPublicSettings;

    const clientHostedLoginURL = orgSettings?.clientHostedLoginURL;
    const clientHostedLoginClientId = orgSettings?.clientHostedLoginClientId;
    const clientHostedLoginScope = orgSettings?.clientHostedLoginScope;
    const useHostedLogin = orgSettings?.useHostedLogin ?? true;
    const logoutUrl = orgSettings?.logoutUrl;
    const subdomain = orgSettings?.subdomain ?? organizationKey;

    if (
      !clientHostedLoginURL ||
      !clientHostedLoginClientId ||
      !clientHostedLoginScope ||
      !subdomain ||
      !useHostedLogin
    ) {
      datadogLogs.logger.error('Exchange Client Code: Hosted Login error', {
        loginError:
          'Client Id and hostedLoginURL are required to initiate Hosted Login',
        organization: organizationKey,
      });
      throw new Error(
        'Client Id and hostedLoginURL are required to initiate Hosted Login',
      );
    }

    setSettings({
      ...settings,
      logoutUrl,
      subdomain,
      useHostedLogin,
      clientHostedLoginURL,
      clientHostedLoginClientId,
      clientHostedLoginRedirectUri:
        orgSettings?.clientHostedLoginRedirectUri || window.location.origin,
    });

    await handleCallback({
      issuerUrl: clientHostedLoginURL,
      redirectUrl: auth0RedirectUri,
      clientId: clientHostedLoginClientId,
      errorHandler: handleHostedLogin,
      successHandler: async (tokenResponse: TokenResponse) => {
        console.log('Exchange client token and retrive machine token');
        externalTokenResponseRef.current = tokenResponse;
        await exchangeClientToken();
      },
    });
  };

  const handleClientHostedTokenFromStorage = async () => {
    if (!clientHostedLoginToken) return;

    const resp = await fetchOrganizationPublicSettings({
      variables: { input: publicSettingsInput },
    });

    const orgSettings = resp?.data?.fetchOrganizationPublicSettings;
    const orgSubdomain = orgSettings?.subdomain ?? organizationKey;

    const clientHostedLoginURL = orgSettings?.clientHostedLoginURL;
    const clientHostedLoginClientId = orgSettings?.clientHostedLoginClientId;

    setSettings({
      ...settings,
      clientHostedLoginURL,
      clientHostedLoginClientId,
      subdomain: orgSubdomain,
      logoutUrl: orgSettings?.logoutUrl,
      useHostedLogin: orgSettings?.useHostedLogin,
      clientHostedLoginRedirectUri:
        orgSettings?.clientHostedLoginRedirectUri || window.location.origin,
    });

    if (!isTokenExpired(clientHostedLoginToken)) {
      // token valid - keep user logged
      oneVestExchangedClientTokenRef.current = clientHostedLoginToken;

      const decodedToken: DecodedToken = jwtDecode(clientHostedLoginToken);

      const externalClientToken = decodedToken[
        'https://onevest.com/externalClientToken'
      ] as string;
      const externalClientRefreshToken = decodedToken[
        'https://onevest.com/externalClientRefreshToken'
      ] as string;

      if (externalClientToken && externalClientRefreshToken) {
        externalTokenResponseRef.current = new TokenResponse({
          access_token: externalClientToken,
          refresh_token: externalClientRefreshToken,
        });
      }

      await fetchPermissions(clientHostedLoginToken);

      localStorage.setItem(LAST_ACTIVITY_TIME_STAMP, new Date().toISOString());

      setLoading(false);
      return;
    }

    // Token is expired - validate SSO session to keep user logged in
    try {
      datadogLogs.logger.warn('Silent SSO Re-use', {
        organization: organizationKey,
        authFlow: 'Client-hosted login token expired, attempting refresh...',
        lastActivityTimeStamp: localStorage.getItem('LAST_ACTIVITY_TIME_STAMP'),
        timeSinceLastActivity: localStorage.getItem('LAST_ACTIVITY_TIME_STAMP')
          ? Date.now() -
            new Date(
              localStorage.getItem('LAST_ACTIVITY_TIME_STAMP') || '',
            ).getTime()
          : 'No last activity time stamp found',
      });

      // This handles silent re-authentication for users
      // who have an active SSO session from a hosted external app.
      const ssoSuccess = await loginWithSSOSession({
        issuerUrl: clientHostedLoginURL,
        redirectUrl: auth0RedirectUri,
        clientId: clientHostedLoginClientId,
        errorHandler: handleHostedLogin,
        successHandler: async (tokenResponse: TokenResponse) => {
          console.log('Exchange client token and retrive machine token');
          externalTokenResponseRef.current = tokenResponse;
          await exchangeClientToken();
        },
      });
      if (ssoSuccess) {
        return;
      }

      // SSO re-use failed, proceed with normal auth flow
      await handleExchangeClientCode();

      localStorage.setItem(LAST_ACTIVITY_TIME_STAMP, new Date().toISOString());
    } catch {
      datadogLogs.logger.error(
        'Client Hosted Token from Storage: Hosted Login error',
        {
          organization: organizationKey,
          loginError: 'Client-hosted login token refresh failed, logging out',
        },
      );
      window.localStorage.removeItem('clientHostedLoginToken');
      await appLogout();
    }
  };

  useEffect(() => {
    getTokenSilentlyRef.current = getTokenSilently;
  }, []);

  useEffect(() => {
    modifyFavIcon(faviconLink);
    modifyTabTitle(browserTabTitle);
  }, [faviconLink, browserTabTitle]);

  useEffect(() => {
    // OVT-5794: honour explicit logout signals in the inbound URL before
    // any silent token acquisition — the silent re-auth path navigates
    // away to the IdP and would drop these params.
    const inboundParams = new URLSearchParams(window.location.search);
    const inboundStatus = (inboundParams.get('status') || '').toUpperCase();
    const inboundLogoutMessage =
      inboundParams.get('loggedOutMessage') ||
      inboundParams.get('logoutMessage') ||
      undefined;
    // Both signals required — blocks drive-by `?logoutMessage=…` phishing. (OVT-5794)
    const isExplicitLogout =
      inboundStatus === 'LOGOUT' && !!inboundLogoutMessage;
    if (isExplicitLogout) {
      datadogLogs.logger.info('Inbound logout signal detected at auth boot', {
        status: inboundStatus || 'IMPLICIT',
        loggedOutMessage: inboundLogoutMessage,
      });

      // OVT-5794: For hosted-login orgs we terminate the IdP SSO session via
      // its end-session endpoint so a refresh on the login screen can't
      // silently re-auth via the still-alive SSO cookie. We can't rely on
      // appLogout's clientHostedLogout branch here because `settings` isn't
      // populated this early in the mount.
      (async () => {
        try {
          const resp = await fetchOrganizationPublicSettings({
            variables: { input: publicSettingsInput },
          });
          const orgSettings = resp?.data?.fetchOrganizationPublicSettings;

          if (
            orgSettings?.useHostedLogin &&
            orgSettings?.clientHostedLoginURL
          ) {
            // Clear stale OV state, then stash the (already-normalized)
            // message under loginPing's canonical key so it gets forwarded
            // to the IdP login screen after the round-trip. Mirrors what
            // appLogout would have done if settings were populated.
            window.localStorage.clear();
            window.localStorage.setItem(
              'loggedOutMessage',
              inboundLogoutMessage,
            );

            // Top-level navigation: OV unloads, the IdP terminates its SSO
            // session, then redirects back to OV where the normal mount flow
            // takes over and surfaces the message via the localStorage stash
            // above. In production OV and `logoutUrl` are on the same origin,
            // so localStorage carries through. For local-dev where they
            // differ, see the override hint in AuthProvider.clientHostedLogout.
            const didNavigate = await clientHostedLogout(
              orgSettings.clientHostedLoginURL,
              orgSettings.logoutUrl ??
                orgSettings.clientHostedLoginRedirectUri ??
                window.location.origin,
            );
            // If the IdP issuer has no end-session endpoint, clientHostedLogout
            // returns false without navigating. Fall through to the fallback
            // below so we don't leave the user stuck on a cleared, stale URL.
            if (didNavigate) return;
          }
        } catch (err) {
          datadogLogs.logger.error(
            'Pre-flight org settings fetch failed; falling back to appLogout',
            { err },
          );
        }

        // Fallback (non-hosted-login orgs, or settings fetch failed):
        // appLogout re-mounts this effect via setResetKey; strip the URL
        // first so the re-mount doesn't loop back into this branch.
        // NB: appLogout at this early mount can't kill the IdP SSO session
        // either (its own settings are empty). prompt=login in loginPing is
        // the safety net that prevents silent IdP re-auth in this path.
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}${window.location.hash}`,
        );
        appLogout({ reason: inboundLogoutMessage });
      })();
      return;
    }

    // check client hosted login token exist in local storage
    if (clientHostedLoginToken) {
      datadogLogs.logger.info('Auth event', {
        organization: organizationKey,
        authFlow: 'Client-hosted login token (local storage)',
      });
      handleClientHostedTokenFromStorage();
      return;
    }

    if (authCode) {
      datadogLogs.logger.info('Auth event', {
        organization: organizationKey,
        authFlow: 'Auth Code exchange process',
      });
      handleExchangeClientCode();
      return;
    }

    if (externalAccessTokenParams && organizationKey) {
      datadogLogs.logger.info('Auth event', {
        organization: organizationKey,
        authFlow: 'Exchanging client token with external access token',
      });
      externalTokenResponseRef.current = new TokenResponse({
        access_token: externalAccessTokenParams,
      });
      exchangeClientToken().catch(() => {
        datadogLogs.logger.error('Exchange client token error', {
          loginError:
            'Error exchanging client token with external access token',
          organization: organizationKey,
        });
      });
      setIsEmbedded(true);
      return;
    }

    if (invitiation) {
      datadogLogs.logger.info('Auth event', {
        organization: organizationKey,
        authFlow: 'Invitation',
      });
      return;
    }

    datadogLogs.logger.info('Auth event', {
      organization: organizationKey,
      authFlow: 'Auth0 flow',
    });

    const handleAccessToMultipleOrgs = (
      payloadAccessibleOrgs: string[],
      newToken: string,
    ) => {
      if (payloadAccessibleOrgs.length > 1) {
        setAccessibleOrgs([...payloadAccessibleOrgs]);
        setOpenOrganizationSelectDialog(true);
        oneVestExchangedClientTokenRef.current = newToken;
        setLoading(false);
      } else {
        auth0.loginWithRedirect({
          authorizationParams: {
            redirect_uri: auth0RedirectUri,
            organization: payloadAccessibleOrgs[0],
            ...(connection ? { connection } : {}),
          },
        });
      }
    };

    const getTokenSilentlyAuth0 = async () => {
      auth0
        .getTokenSilently()
        .then(async (newToken: string) => {
          const payload = parseJwt(newToken);
          const payloadAccessibleOrgs =
            payload[`${JWT_CLAIMS_NAMESPACE}/accessibleOrgs`];
          const payloadRefreshTokenRequired =
            payload[`${JWT_CLAIMS_NAMESPACE}/refreshTokenRequired`];

          const resp = await fetchOrganizationPublicSettings({
            variables: { input: publicSettingsInput },
          });

          const orgSettings = resp?.data?.fetchOrganizationPublicSettings;

          if (orgSettings) {
            setSettings({
              ...settings,
              logoutUrl: orgSettings?.logoutUrl,
              useHostedLogin: orgSettings?.useHostedLogin,
              subdomain: orgSettings?.subdomain ?? organizationKey,
              clientHostedLoginURL: orgSettings?.clientHostedLoginURL,
              clientHostedLoginClientId: orgSettings?.clientHostedLoginClientId,
              clientHostedLoginRedirectUri:
                orgSettings?.clientHostedLoginRedirectUri ||
                window.location.origin,
            });
          }

          if (linkExpiredError && linkExpiredErrorDescription) {
            datadogLogs.logger.error(
              'Get Token Silently: Invitation link expired',
              {
                loginError: 'Invitation link expired',
              },
            );
            throw new Error('Invitation link expired');
          }

          if (payloadAccessibleOrgs?.length > 0 && !multipleOrgChosen) {
            handleAccessToMultipleOrgs(payloadAccessibleOrgs, newToken);
          } else {
            oneVestExchangedClientTokenRef.current = newToken;
            const orgUserExists = await fetchPermissions(newToken);
            if (payloadRefreshTokenRequired && orgUserExists) {
              // Refresh token may be needed in case the Auth0 user app_metadata was synched after the current token was issued
              auth0RefreshToken({ orgSubdomain: payload.org_name });
            } else {
              setLoading(false);
            }
          }
        })
        .catch(async () => {
          // The user is not authenticated and a fresh login is starting.
          // Clear any stale org-selection flag left over from a previous
          // session (e.g. logout paths that bypass appLogout) so the
          // org-select dialog is re-evaluated for the new session.
          window.localStorage.removeItem('multipleOrgChosen');

          const resp = await fetchOrganizationPublicSettings({
            variables: { input: publicSettingsInput },
          });

          const orgSettings = resp?.data?.fetchOrganizationPublicSettings;
          const subdomain = orgSettings?.subdomain;

          const orgConnectionName =
            orgSettings?.enterpriseConnectionName || connection;

          if (orgSettings) {
            setSettings({
              ...settings,
              logoutUrl: orgSettings?.logoutUrl,
              subdomain: orgSettings?.subdomain ?? organizationKey,
              useHostedLogin: orgSettings?.useHostedLogin,
              clientHostedLoginURL: orgSettings?.clientHostedLoginURL,
              clientHostedLoginClientId: orgSettings?.clientHostedLoginClientId,
              clientHostedLoginRedirectUri:
                orgSettings?.clientHostedLoginRedirectUri ||
                window.location.origin,
            });
          }

          if (resp && orgSettings) {
            setOrgPublicSettings(
              orgSettings,
              setFaviconLink,
              setBrowserTabTitle,
            );
          }
          if (orgSettings?.useHostedLogin) {
            handleHostedLogin();
            return;
          }
          const authParams = {
            authorizationParams: {
              redirect_uri: auth0RedirectUri,
              ...(subdomain ? { organization: subdomain } : {}),
              ...(orgConnectionName ? { connection: orgConnectionName } : {}),
            },
          };

          if (linkExpiredError && linkExpiredErrorDescription) {
            setLoginParams(authParams);
            setLoading(false);
            setOpenLinkExpiredDialog(true);
            return;
          }

          if (allowPopup === 'true') {
            auth0.loginWithPopup(authParams).then((value) => {
              console.log({ value });
            });
          } else {
            auth0.loginWithRedirect(authParams);
          }
        });
    };

    if (state) {
      auth0
        .handleRedirectCallback()
        .then(() => {
          auth0
            .getTokenSilently()
            .then(async (newToken: string) => {
              const payload = parseJwt(newToken);
              const resp = await fetchOrganizationPublicSettings({
                variables: { input: { subdomain: payload.org_name } },
              });

              const orgSettings = resp?.data?.fetchOrganizationPublicSettings;

              if (orgSettings) {
                setSettings({
                  ...settings,
                  logoutUrl: orgSettings?.logoutUrl,
                  useHostedLogin: orgSettings?.useHostedLogin,
                  subdomain: orgSettings?.subdomain ?? organizationKey,
                  clientHostedLoginURL: orgSettings?.clientHostedLoginURL,
                  clientHostedLoginClientId:
                    orgSettings?.clientHostedLoginClientId,
                  clientHostedLoginRedirectUri:
                    orgSettings?.clientHostedLoginRedirectUri ||
                    window.location.origin,
                });
              }

              if (resp && orgSettings) {
                setOrgPublicSettings(
                  orgSettings,
                  setFaviconLink,
                  setBrowserTabTitle,
                );
              }

              const payloadAccessibleOrgs =
                payload[`${JWT_CLAIMS_NAMESPACE}/accessibleOrgs`];
              const payloadRefreshTokenRequired =
                payload[`${JWT_CLAIMS_NAMESPACE}/refreshTokenRequired`];
              if (payloadAccessibleOrgs?.length > 0 && !multipleOrgChosen) {
                handleAccessToMultipleOrgs(payloadAccessibleOrgs, newToken);
              } else {
                console.log({
                  event: 'AUTHENTICATED_USER',
                  userReferenceId: payload.sub,
                });
                oneVestExchangedClientTokenRef.current = newToken;
                const orgUserExists = await fetchPermissions(newToken);
                if (payloadRefreshTokenRequired && orgUserExists) {
                  // Refresh token may be needed in case the Auth0 user app_metadata was synched after the current token was issued
                  auth0RefreshToken({ orgSubdomain: payload.org_name });
                } else {
                  setLoading(false);
                  const path = window.localStorage.getItem('auth0RedirectPath');
                  if (path) {
                    const auth0RedirectPath = JSON.parse(path);
                    if (dayjs().isBefore(auth0RedirectPath.expireAt)) {
                      const redirectPath = auth0RedirectPath.locationPath;
                      window.history.replaceState(null, '', redirectPath);
                    }
                    window.localStorage.removeItem('auth0RedirectPath');
                  }
                }
              }
            })
            .catch(async (error) => {
              console.log({ error });
              // The user is not authenticated and a fresh login is starting.
              // Clear any stale org-selection flag left over from a previous
              // session (e.g. logout paths that bypass appLogout) so the
              // org-select dialog is re-evaluated for the new session.
              window.localStorage.removeItem('multipleOrgChosen');

              const resp = await fetchOrganizationPublicSettings({
                variables: { input: publicSettingsInput },
              });

              const orgSettings = resp?.data?.fetchOrganizationPublicSettings;

              const subdomain = orgSettings?.subdomain;
              const orgConnectionName =
                orgSettings?.enterpriseConnectionName || connection;

              if (orgSettings) {
                setSettings({
                  ...settings,
                  logoutUrl: orgSettings?.logoutUrl,
                  useHostedLogin: orgSettings?.useHostedLogin,
                  subdomain: orgSettings?.subdomain ?? organizationKey,
                  clientHostedLoginURL: orgSettings?.clientHostedLoginURL,
                  clientHostedLoginClientId:
                    orgSettings?.clientHostedLoginClientId,
                  clientHostedLoginRedirectUri:
                    orgSettings?.clientHostedLoginRedirectUri ||
                    window.location.origin,
                });
              }

              if (orgSettings) {
                setOrgPublicSettings(
                  orgSettings,
                  setFaviconLink,
                  setBrowserTabTitle,
                );
              }
              auth0.loginWithRedirect({
                authorizationParams: {
                  redirect_uri: auth0RedirectUri,
                  ...(subdomain ? { organization: subdomain } : {}),
                  ...(orgConnectionName
                    ? { connection: orgConnectionName }
                    : {}),
                },
              });
            });
        })
        .catch(() => {
          getTokenSilentlyAuth0();
        });
    } else {
      getTokenSilentlyAuth0();
    }
  }, []);

  return (
    <>
      {loading ? (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          height='100vh'
          margin='auto'
        >
          <CircularProgress />
        </Box>
      ) : accessibleOrgs && accessibleOrgs.length > 0 && !multipleOrgChosen ? (
        <PublicOrganizationSelectDialog
          open={openOrganizationSelectDialog}
          onOrgSelect={onOrgSelect}
          filter={{ referenceIds: accessibleOrgs }}
        />
      ) : openSignUpNotAllowedDialog ? (
        <AuthDialog
          title={t('signUpNotAllowedDialog.title')}
          body={t('signUpNotAllowedDialog.body')}
          onClose={appLogout}
        />
      ) : openLinkExpiredDialog ? (
        <AuthDialog
          title={t('linkExpiredDialog.title')}
          body={t('linkExpiredDialog.body')}
          onClose={() => auth0.loginWithRedirect(loginParams)}
        />
      ) : (
        children
      )}
    </>
  );
};

export default Auth0Provider;

const AuthDialog = ({
  title,
  body,
  onClose,
}: {
  title: string;
  body: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation('shared');

  return (
    <Dialog open={true} fullWidth maxWidth='xs'>
      <DialogTitle
        display='flex'
        alignItems='center'
        justifyContent='space-between'
      >
        <Typography variant='titleLarge'>{title}</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant='bodyLarge'>{body}</Typography>
        <Box mt={5}>
          <Button
            type='button'
            fullWidth
            variant='filled'
            onClick={onClose}
            label={t('shared:okGotIt')}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};
